/**
 * Client for the license binding registry (see /worker). Enforces "one active
 * browser per purchase":
 *   - activate() claims the active-browser slot on redeem.
 *   - check() asks whether this browser still holds the slot (called on load).
 *
 * When LICENSE_API_URL is empty the registry is disabled and both calls report
 * "enabled: false" so callers fall back to offline-only behaviour.
 */
import { LICENSE_API_URL } from "../core/index.ts";

export type CheckResult =
  | { enabled: false }                 // registry not configured
  | { enabled: true; reachable: false } // network/server error — caller uses grace
  | { enabled: true; reachable: true; active: boolean };

async function post(path: string, token: string): Promise<CheckResult> {
  if (!LICENSE_API_URL) return { enabled: false };
  try {
    const res = await fetch(`${LICENSE_API_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) return { enabled: true, reachable: false };
    const data = (await res.json()) as { ok?: boolean; active?: boolean };
    return { enabled: true, reachable: true, active: data.active === true };
  } catch {
    return { enabled: true, reachable: false };
  }
}

/** Claim the active-browser slot for this code's purchase. */
export function activate(token: string): Promise<CheckResult> {
  return post("/activate", token);
}

/** Is this browser still the active one for its purchase? */
export function check(token: string): Promise<CheckResult> {
  return post("/verify", token);
}
